import React from 'react';
import { useRSVPForm } from '../hooks/useRSVPForm.ts';
import { ColorScheme } from '../constants/colors.ts';
import { CardConfig } from '../constants/cards.ts';

interface RSVPSectionProps {
  rsvpSectionRef: React.RefObject<HTMLElement | null>;
  cardConfig: CardConfig;
  colors: ColorScheme;
}

const RSVPSection: React.FC<RSVPSectionProps> = ({ rsvpSectionRef, cardConfig, colors }) => {
  const { wishesData, setWishesData, isSubmitting, submitError, handleSubmit } = useRSVPForm(cardConfig);
  const [isAttending, setIsAttending] = React.useState<boolean | null>(null);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // If the number doesn't start with country code and has 10-11 digits, add Malaysia country code
    if (cleaned.length >= 8 && cleaned.length <= 11 && !cleaned.startsWith('60')) {
      return '+6' + cleaned;
    }
    
    // If it already has country code or is too short/long, return as is with + prefix
    if (cleaned.length > 0 && !value.startsWith('+')) {
      return '+' + cleaned;
    }
    
    return value;
  };

  return (
    <section 
      ref={rsvpSectionRef}
      className="py-20 relative overflow-hidden z-[1]"
      style={{ 
        background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
      }}
    >
      <div className="px-4 relative z-[2]">
        {/* Only show header when not "Not Attending" */}
        {isAttending !== false && (
          <>
            <h3 className="font-optima text-2xl text-center mb-2" style={{ color: colors.secondary }}>
              Attendance Confirmation
            </h3>
            <div className="w-12 h-[1px] mx-auto mb-4" style={{ backgroundColor: colors.accent }}></div>
            <p className="font-optima text-sm text-center mb-12 tracking-wide" style={{ color: colors.textLight }}>
              Please confirm your attendance
            </p>
          </>
        )}
        
        {/* Attendance Selection Buttons */}
        {isAttending === null && (
          <div className="max-w-sm mx-auto flex gap-3 justify-center">
            <button
              onClick={() => setIsAttending(true)}
              className="flex-1 font-semibold py-3 px-6 rounded-full transition-all text-sm shadow-lg border-2"
              style={{
                backgroundColor: colors.secondary,
                color: colors.primary,
                borderColor: colors.accent,
                boxShadow: `0 4px 15px ${colors.shadow}`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.secondaryHover;
                e.currentTarget.style.borderColor = colors.accentHover;
                e.currentTarget.style.boxShadow = `0 6px 20px ${colors.shadow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.secondary;
                e.currentTarget.style.borderColor = colors.accent;
                e.currentTarget.style.boxShadow = `0 4px 15px ${colors.shadow}`;
              }}
            >
              Attending
            </button>
            <button
              onClick={() => setIsAttending(false)}
              className="flex-1 font-semibold py-3 px-6 rounded-full transition-all text-sm shadow-lg border-2"
              style={{
                backgroundColor: 'transparent',
                color: colors.secondary,
                borderColor: colors.secondary,
                boxShadow: `0 4px 15px ${colors.shadow}`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.secondary}20`;
                e.currentTarget.style.borderColor = colors.accent;
                e.currentTarget.style.boxShadow = `0 6px 20px ${colors.shadow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = colors.secondary;
                e.currentTarget.style.boxShadow = `0 4px 15px ${colors.shadow}`;
              }}
            >
              Not Attending
            </button>
          </div>
        )}

        {/* Thank You Message for "Not Attending" */}
        {isAttending === false && (
          <div className="max-w-md mx-auto text-center space-y-6">
            <p className="font-optima text-lg" style={{ color: colors.secondary }}>
              Thank you for your response!
            </p>
          </div>
        )}
        
        {/* RSVP Form - Only shown if attending */}
        {isAttending === true && (
          <div className="max-w-md mx-auto">
            <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-8">
              <div>
                <input
                  type="text"
                  required
                  placeholder="Your Name"
                  className="w-full px-4 py-3 text-base bg-transparent border-b-2 transition duration-200"
                  style={{
                    borderColor: colors.border,
                    color: colors.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                  value={wishesData.name}
                  onChange={(e) => setWishesData({...wishesData, name: e.target.value})}
                />
              </div>
              
              <div>
                <input
                  type="tel"
                  required
                  placeholder="Phone Number"
                  className="w-full px-4 py-3 text-base bg-transparent border-b transition duration-200"
                  style={{
                    borderColor: `${colors.secondary}4D`, // 30% opacity
                    color: colors.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => {
                    e.target.style.borderColor = `${colors.secondary}4D`;
                    // Auto-format phone number on blur
                    const formatted = formatPhoneNumber(e.target.value);
                    if (formatted !== e.target.value) {
                      setWishesData({...wishesData, phone: formatted});
                    }
                  }}
                  value={wishesData.phone}
                  onChange={(e) => setWishesData({...wishesData, phone: e.target.value})}
                />
              </div>
              
              {/* Group field - commented out */}
              {/* <div className="relative">
                <select
                  required
                  className="w-full px-4 py-3 text-base border-b-2 transition duration-200 appearance-none cursor-pointer bg-transparent"
                  style={{
                    borderColor: colors.border,
                    color: colors.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                  value={wishesData.organization}
                  onChange={(e) => {
                    const newOrganization = e.target.value;
                    const maxPax = newOrganization === 'Family' ? 10 : 2;
                    setWishesData({
                      ...wishesData, 
                      organization: newOrganization,
                      pax: wishesData.pax > maxPax ? 1 : wishesData.pax
                    });
                  }}
                >
                  <option value="" style={{ backgroundColor: colors.primary, color: `${colors.secondary}80` }}>
                    Group
                  </option>
                  {cardConfig.organizationOptions.map((option) => (
                    <option key={option} value={option} style={{ backgroundColor: colors.primary }}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 px-2" style={{ color: colors.secondary }}>
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div> */}
              
              {/* Invitation From field - commented out */}
              {/* <div className="relative">
                <select
                  required
                  className="w-full px-4 py-3 text-base border-b-2 transition duration-200 appearance-none cursor-pointer bg-transparent"
                  style={{
                    borderColor: colors.border,
                    color: colors.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                  value={wishesData.invitationFrom}
                  onChange={(e) => setWishesData({...wishesData, invitationFrom: e.target.value})}
                >
                  <option value="" style={{ backgroundColor: colors.primary, color: `${colors.secondary}80` }}>
                    Invitation From
                  </option>
                  {cardConfig.invitationFromOptions.map((option) => (
                    <option key={option} value={option} style={{ backgroundColor: colors.primary }}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 px-2" style={{ color: colors.secondary }}>
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div> */}
              
              <div className="relative">
                <select
                  required
                  className="w-full px-4 py-3 text-base border-b-2 transition duration-200 appearance-none cursor-pointer bg-transparent"
                  style={{
                    borderColor: colors.border,
                    color: colors.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                  value={wishesData.pax}
                  onChange={(e) => setWishesData({...wishesData, pax: parseInt(e.target.value) || 1})}
                >
                  {(() => {
                    const maxPax = 10; // Always 10 pax
                    return Array.from({length: maxPax}, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num} style={{ backgroundColor: colors.primary }}>
                        {num} pax
                      </option>
                    ));
                  })()}
                </select>
                <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 px-2" style={{ color: colors.secondary }}>
                  <svg className="fill-current h-4 w-4 mb-8" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>
            
            {submitError && (
              <p className="text-sm text-center" style={{ color: colors.error }}>
                {submitError}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full font-semibold py-4 rounded-full transition-all text-lg shadow-lg disabled:opacity-60 border-2"
              style={{
                backgroundColor: colors.secondary,
                color: colors.primary,
                borderColor: colors.accent,
                boxShadow: `0 4px 15px ${colors.shadow}`
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = colors.secondaryHover;
                  e.currentTarget.style.borderColor = colors.accentHover;
                  e.currentTarget.style.boxShadow = `0 6px 20px ${colors.shadow}`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = colors.secondary;
                  e.currentTarget.style.borderColor = colors.accent;
                  e.currentTarget.style.boxShadow = `0 4px 15px ${colors.shadow}`;
                }
              }}
            >
              {isSubmitting ? 'Confirming...' : 'Confirm RSVP'}
            </button>
          </form>
        </div>
        )}
      </div>
    </section>
  );
};

export default RSVPSection;
